<template>
<div v-if="plusPlans.length" class="pricing-table-component">

  <div class="plans">

    <!-- ENTERPRISE DISPLAY -->
    <div v-if="isEnterprisePlan == true" class="pa-4 plan contrast">
      <DynamicLogo class="logo" />
      <br/>
      You are currently on PM2 Enterprise plan.<br/>
      <br/>
      <h2>Current plan</h2>
      <div class="infos-plan mt-2">
        Type:<br/>
        <b>{{ currentPlan.name }}</b>
        <br />
        Plan Monthly Price:
        <br/>
        <b>{{ formatPlanPrice(currentPlan) }}</b>
        <br />
        Process Limit number:<br/>
        <b>{{ currentPlan.process_limit }}</b>
        <br />
        Contact:<br/>
        <b>sales@keymetrics.io</b>
      </div>
    </div>

    <!-- SELF SERVICE DISPLAY -->
    <div v-else="!upgrade" :class="`${dark ? 'contrast' : 'base'} pa-2 pt-4 plan`">
      <DynamicLogo class="logo" />
      <div class="plan-expl">
        <p class="pm2-plus-tagline mt-3 mb-4">
          Improve your Node.js application and check that it runs smoothly 24/24 7/7 with PM2
          Plus:
        </p>
        <ul class="features">
          <li v-for="incentive in incentives.plus" :key="incentive.id">
            <KmIcon :name="incentive.route.meta.icon" />&nbsp;&nbsp;{{ incentive.title }}
          </li>
          <li><KmIcon :size="28" name="km-icon-mcp" /> MCP / AI Assistant</li>
          <li><VIcon color="white">mdi-clock</VIcon>&nbsp;&nbsp; 14 days Data Retention</li>
        </ul>
      </div>

      <div class="price-zone">

        <h1 class="price-title">
          {{ formatPlanPrice(selectedPlusPlan) }}<small>/month</small>
        </h1>

        <VSlider
          v-model="processPlan"
          min="0"
          :max="plusPlans.length - 1"
          :alwaysDirty="true"
          step="1"
          color="info"
          ticks
          class="pa-0 ma-0 px-5 no-disable slider-override"
          />
        <p class="price-limit mb-2">
          <small>Monitor up to</small><br />
          {{ selectedPlusPlan.process_limit }} processes
        </p>

        <div class="yearly-switch">
          <small v-if="yearly">
            Yearly
          </small>
          <small v-else>
            Monthly
          </small>
          <VSwitch
            v-model="yearly"
            class="no-top-space"
            hideDetails
            color="info"
            />
          <small v-if="yearly">
            -20%
          </small>
        </div>

        <div v-for="line in plusButtonHint" :key="line" class="plus--text">
          <strong>{{ line }}</strong>
        </div>

        <VBtn color="green"
              class="font-bold mt-2 mx-0 mb-0"
              @click="subscribe()">
          Subscribe to Plan
        </VBtn>

        <span v-if="isPremiumBucket"
              @click="unsubscribe()"
              class="unsubscribe"
              color="error">
          Unsubscribe
        </span>

      </div>
    </div>
  </div>
</div>
</template>

<script>
import DynamicLogo from '@/components/DynamicLogo'
import ContactButton from './ContactButton'
import countPayingProcesses from '@/utils/countPayingProcesses'
import { formatDate } from '@/utils/format'
import { getIncentives } from '@/utils/routeHelpers'
import KmIcon from '@/components/KmIcon'

const PROCESS_LIMIT_MAX = 32
const PLAN_VERSION = 2

export default {
  name: 'PricingTableComponent',
  components: {
    DynamicLogo,
    KmIcon,
    ContactButton,
  },
  props: {
    handleSubscribe: {
      name: 'handleSubscribe',
      type: Function,
    },
    upgrade: {
      name: 'upgrade',
      type: Boolean,
    },
    dark: {
      name: 'dark',
      type: Boolean,
    },
  },

  data() {
    return {
      processPlan: 0,
      contactInfos: undefined,
      isElligibleForTrial: undefined,
      yearly: false,
      subscription: null,
    }
  },

  computed: {
    bucket() {
      return this.$store.getters.currentBucket
    },
    incentives() {
      return getIncentives(this.$router)
    },

    plans() {
      return this.$store.getters.plans
    },

    plusPlans() {
      return Object.values(this.plans).filter(
        plan =>
          plan.is_premium && this.isPlanAvailable(plan) && (plan.interval === 'year') === this.yearly,
      )
    },

    isEnterprisePlan() {
      return this.$store.getters.currentPlanType == 'enterprise'
    },

    isPremiumBucket() {
      const type = this.$store.getters.currentPlanType
      if (type === 'plus' || type === 'enterprise') {
        return true
      }
      return false
    },

    currentPlan() {
      return this.$store.getters.currentPlan
    },

    trialPlans() {
      return this.$store.getters.trialPlans
    },

    selectedPlusPlanIsTrial() {
      return (
        this.isElligibleForTrial &&
        this.trialPlans.some(other => this.selectedPlusPlan.id === other.id)
      )
    },

    selectedPlusPlan() {
      return this.plusPlans[this.processPlan]
    },

    plusButtonHint() {
      if (this.currentPlan.name === 'enterprise') return []
      if (!this.currentPlan.is_premium) return []

      const lines = []

      const processes = this.currentPlan.process_limit

      if (this.subscription && this.subscription.status === 'trialing') {
        lines.push(`Current trial: up to ${processes} processes.`)

        if (this.subscription.cancel_at_period_end) {
          lines.push(`Deactivating on ${formatDate(new Date(this.subscription.trial_end * 1000))},`)
        } else {
          lines.push(
            `Migrating to a paid plan on ${formatDate(
              new Date(this.subscription.trial_end * 1000),
            )},`,
          )
        }
      } else {
        lines.push(`Current plan: up to ${processes} processes.`)

        if (this.subscription && this.subscription.cancel_at_period_end) {
          lines.push(
            `Ending on ${formatDate(new Date(this.subscription.current_period_end * 1000))}`,
          )
        }
      }

      return lines
    },

  },
  beforeMount() {
    Promise.all([
      this.$store.getters.isElligibleForTrial(),
      this.$store.getters.currentBillingSubscription,
    ]).then(([isElligibleForTrial, subscription]) => {
      this.isElligibleForTrial = isElligibleForTrial

      const targetPlan = this.getTargetPlan()

      this.yearly = targetPlan.interval === 'year'
      const planIndex = this.plusPlans.findIndex(plan => plan.id === targetPlan.id)

      if (planIndex === -1) {
        this.processPlan = 0
      } else {
        this.processPlan = planIndex
      }

      this.subscription = subscription
    })
  },

  methods: {
    isNewGenPlan() {
      return this.bucket.bucket && this.bucket.bucket.payment && this.bucket.bucket.payment.offer_type && this.bucket.bucket.payment.offer_type.indexOf('_v3') != -1
    },
    isPlanAvailable(plan) {
      return (
        ((plan.publicly_available === true && this.isNewGenPlan() == false && plan.show_to_new_user != true && this.$store.getters.currentPlanType == 'plus') ||
         (plan.show_to_new_user === true && (this.$store.getters.currentPlanType != 'plus' || this.isNewGenPlan()))) &&
          plan.version === PLAN_VERSION &&
          plan.process_limit <= PROCESS_LIMIT_MAX
      )
    },
    formatPlanPrice(plan) {
      let amount = plan.amount / 100
      if (plan.interval === 'year') {
        amount /= 12
      }
      return `$${Math.ceil(amount)}`
    },
    getTargetPlan() {
      const currentBucket = this.$store.getters.currentBucket

      if (this.upgrade && currentBucket) {
        const plans = this.isElligibleForTrial ? this.trialPlans : this.plusPlans

        let neededProcesses =
          countPayingProcesses(
            this.$store.getters.select({
              appName: false,
              onlineOnly: true,
            }).processes,
          ) + currentBucket.processBlacklist.length

        // If the current plan is enough, try to upsell
        if (this.currentPlan.process_limit >= neededProcesses) {
          neededProcesses = this.currentPlan.process_limit + 1
        }

        return plans.find(plan => plan.process_limit >= neededProcesses) || plans[plans.length - 1]
      }

      return this.currentPlan
    },

    subscribe() {
      this.handleSubscribe(this.selectedPlusPlan, false)
    },

    unsubscribe() {
      this.handleSubscribe(this.plans.free, false)
    },
  },
}
</script>

<style lang="postcss">
.pricing-table-component {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;

  & .slider-override {
    & .v-messages {
      display: none;
    }
  }
  & small {
    font-size: var(--fs-caption);
  }

  & .v-slider {
    &:hover {
      cursor: pointer;
    }
  }

  & .slider__thumb {
    width: 20px !important;
    height: 20px !important;
    &:hover {
      cursor: pointer;
    }
  }

  & .plans {
    display: flex;
    position: relative;
    width: 100%;
    max-width: 400px;

    & .plan {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }

  & .plan-expl {
    flex: 1;

    & h3 {
      height: 4.5em;
    }
  }

  & .price-zone {
    position: relative;
    margin-top: 0px;
    padding: 15px;
    & .price-title {
      margin: 0;
      margin-top: 5px;
      font-size: var(--fs-display-1);
    }

    & .price-limit {
      padding: 0;
      margin: 0;
      font-size: var(--fs-title);
    }
    & .yearly-switch {
      position: absolute;
      top: 22px;
      right: 10px;
    }
  }
}

.current-plan {
  text-align: left;
  margin-left: 46px;
}

.pm2-plus-tagline {
  font-size: var(--fs-body-1);
}

.pricing-table-component .logo {
  height: 48px;
  width: auto;
  align-self: center;
}

ul.features {
  border: 2px solid var(--contrast);
  margin-left: 68px;
  width: 250px;
}

.infos-plan {
  font-size: var(--fs-body-1);
  & b {
    font-size: var(--fs-title);
    display: inline-block;
    margin-bottom: 10px;
  }
}

.features {
  & li {
    text-align: left;

    padding: 8px 16px;
    font-size: var(--fs-body-1);
    background-color: var(--contrast);
    &:nth-child(2n) {
      background-color: var(--base);
    }
  }

  & .v-icon {
    color: #fbcd43;
    font-size: var(--fs-title);
  }
}

.unsubscribe {
  color: var(--red);
  margin-top: 10px;
  display: block;
  &:hover {
    opacity: 0.4;
    cursor:pointer;
  }
}
</style>
